import { isMfaPath, MFA_SETUP_PATH, MFA_VERIFY_PATH } from "./constants";

export type MfaAssuranceLevel = "aal1" | "aal2" | null;

export type MfaFactor = {
  id: string;
  status?: string;
};

export type MfaFactors = {
  totp: MfaFactor[];
  phone: MfaFactor[];
};

export function countVerifiedFactors(factors: MfaFactors): number {
  const all = [...(factors.totp ?? []), ...(factors.phone ?? [])];
  return all.filter((factor) => factor.status === "verified").length;
}

export function hasVerifiedMfaFactor(factors: MfaFactors): boolean {
  return countVerifiedFactors(factors) > 0;
}

/**
 * Password/email updates require a real AAL2 session when MFA is enrolled.
 * Trusted-device AAL1 access is not enough for these sensitive auth changes.
 */
export function needsPasswordMfaStepUp(params: {
  currentLevel: MfaAssuranceLevel;
  factors: MfaFactors;
}): boolean {
  const { currentLevel, factors } = params;
  if (!hasVerifiedMfaFactor(factors)) return false;
  return currentLevel !== "aal2";
}

/**
 * After MFA.verify on the set-password page, the client already holds an AAL2
 * session. Re-applying recovery tokens from the URL would downgrade to AAL1 and
 * block the password update — keep the stepped-up session instead.
 */
export function shouldReuseSessionForPasswordUpdate(params: {
  hasCurrentSession: boolean;
  currentLevel: MfaAssuranceLevel;
  factors: MfaFactors;
}): boolean {
  return (
    params.hasCurrentSession &&
    hasVerifiedMfaFactor(params.factors) &&
    params.currentLevel === "aal2"
  );
}

/**
 * Pure MFA redirect logic used by checkAuth.
 * Returns a redirect path or null when MFA requirements are satisfied.
 */
export function getMfaRedirect(params: {
  currentLevel: MfaAssuranceLevel;
  nextLevel: MfaAssuranceLevel;
  factors: MfaFactors;
  isTrustedDevice: boolean;
  pathname: string;
}): string | null {
  const { currentLevel, nextLevel, factors, isTrustedDevice, pathname } =
    params;

  if (isMfaPath(pathname)) {
    return null;
  }

  if (!hasVerifiedMfaFactor(factors)) {
    return MFA_SETUP_PATH;
  }

  if (currentLevel === "aal1" && nextLevel === "aal2" && !isTrustedDevice) {
    return MFA_VERIFY_PATH;
  }

  return null;
}

export { MFA_SETUP_PATH, MFA_VERIFY_PATH };
